import { Box, Paper, Typography, useTheme } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { tokens } from "../../../theme";
import { CrewIcon } from "../../../components/icons";
import { ConfigurationIcon } from "../../../layout/sidebar/icons";
import type { OpsStats } from "../types/opsDashboard";

interface StatCardProps {
  value: number | string;
  label: string;
  icon: React.ReactNode;
  color: string;
}

function StatCard({ value, label, icon, color }: StatCardProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minWidth: "100px",
        p: 1.5,
        borderRadius: "16px",
        backgroundColor: colors.primary[900],
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: "50%",
          backgroundColor: `${color}15`,
          color: color,
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="h4"
        fontWeight={900}
        color={colors.primary[100]}
        sx={{ mt: 0.5 }}
      >
        {value}
      </Typography>
      <Typography
        variant="h6"
        fontWeight={600}
        color={color}
        sx={{ whiteSpace: "nowrap" }}
      >
        {label}
      </Typography>
    </Paper>
  );
}

interface StatCardsDashboardProps {
  stats?: OpsStats;
}

export default function StatCardsDashboard({ stats }: StatCardsDashboardProps) {
  return (
    <Box sx={{ display: "flex", gap: 2, width: "100%", flexWrap: "wrap" }}>
      <StatCard
        value={stats?.completed_streams ?? "—"}
        label="Completed Streams"
        icon={<CheckCircleOutlineIcon fontSize="small" />}
        color="#2ec4b6"
      />
      <StatCard
        value={stats?.upcoming_streams ?? "—"}
        label="Upcoming Streams"
        icon={<AccessTimeIcon fontSize="small" />}
        color="#ff9f1c"
      />
      <StatCard
        value={stats?.active_pixel_pilots ?? "—"}
        label="Active Pilots"
        icon={<CrewIcon fontSize="small" />}
        color="#3a86ff"
      />
      <StatCard
        value={stats?.gear_available ?? "—"}
        label="Gear Available"
        icon={<ConfigurationIcon fontSize="small" />}
        color="#20bf6b"
      />
    </Box>
  );
}
